import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { PropertyThumbnail, Section, StatTile } from "./drawer-primitives";
import { formatCityLine, PropertyMeta } from "@/lib/property-meta";
import { PAYMENT_METHODS, PAYMENT_STATUSES } from "./payment-options";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  CircleDollarSign,
  Home,
  Loader2,
  NotebookPen,
  Wallet,
  X,
} from "lucide-react";
import { RentPayment } from "@/hooks/use-property-management";
import { format, parseISO } from "date-fns";

/** Statuses that mean nothing has been received yet — a paid date can't apply. */
const UNPAID_STATUSES = new Set(["pending", "late"]);

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    headline: string;
    chip: string;
    accent: string;
    icon: React.ReactNode;
  }
> = {
  pending: {
    label: "Pending",
    headline: "Awaiting payment",
    chip: "bg-muted text-muted-foreground",
    accent: "border-border bg-muted/40",
    icon: <Clock className="h-3 w-3" />,
  },
  paid: {
    label: "Paid",
    headline: "Payment received",
    chip: "bg-success/15 text-success",
    accent: "border-success/30 bg-success/10",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  late: {
    label: "Late",
    headline: "Payment overdue",
    chip: "bg-destructive/15 text-destructive",
    accent: "border-destructive/30 bg-destructive/10",
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  partial: {
    label: "Partial",
    headline: "Partially paid",
    chip: "bg-warning/15 text-warning",
    accent: "border-warning/30 bg-warning/10",
    icon: <CircleDollarSign className="h-3 w-3" />,
  },
};

interface PaymentDetailDrawerProps {
  payment: RentPayment | null;
  open: boolean;
  /** Photo and address of the property this payment is against. */
  property?: PropertyMeta;
  /** Selectable properties — includes this payment's own property even if its listing is no longer rented. */
  propertyOptions: { id: string; label: string }[];
  onOpenChange: (open: boolean) => void;
  onUpdate: (p: Partial<RentPayment> & { id: string }) => void;
  isSaving: boolean;
}

type Draft = Pick<
  RentPayment,
  | "pipeline_property_id"
  | "tenant_name"
  | "amount"
  | "due_date"
  | "paid_date"
  | "payment_method"
  | "status"
  | "late_fee"
  | "notes"
>;

function toDraft(p: RentPayment): Draft {
  return {
    pipeline_property_id: p.pipeline_property_id,
    tenant_name: p.tenant_name,
    amount: p.amount,
    due_date: p.due_date,
    paid_date: p.paid_date,
    payment_method: p.payment_method,
    status: p.status,
    late_fee: p.late_fee,
    notes: p.notes,
  };
}

const money = (n: number | null | undefined) =>
  n != null ? `$${n.toLocaleString()}` : "—";
const today = () => format(new Date(), "yyyy-MM-dd");
const prettyDate = (d: string | null) =>
  d ? format(parseISO(d), "MMM d, yyyy") : "—";

export function PaymentDetailDrawer({
  payment,
  open,
  property,
  propertyOptions,
  onOpenChange,
  onUpdate,
  isSaving,
}: PaymentDetailDrawerProps) {
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    setDraft(payment ? toDraft(payment) : null);
    // Reset the draft only when a different payment is opened, so a background
    // refetch never wipes edits in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.id]);

  if (!payment || !draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button:last-of-type]:hidden"
        >
          <SheetTitle className="sr-only">Payment</SheetTitle>
        </SheetContent>
      </Sheet>
    );
  }

  const cfg = STATUS_CONFIG[draft.status] ?? STATUS_CONFIG.pending;
  // The drawer stays open while the property select changes, so prefer the
  // option label for the drafted property over the meta of the saved one.
  const propertyLabel =
    propertyOptions.find((o) => o.id === draft.pipeline_property_id)?.label ??
    property?.address;
  const isSavedProperty =
    draft.pipeline_property_id === payment.pipeline_property_id;
  const cityLine = isSavedProperty ? formatCityLine(property) : "";
  const amountValid = Number.isFinite(draft.amount) && draft.amount > 0;
  const canSave =
    amountValid &&
    !!draft.tenant_name.trim() &&
    !!draft.pipeline_property_id &&
    !!draft.due_date;
  const isDirty = JSON.stringify(draft) !== JSON.stringify(toDraft(payment));

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const numOrZero = (v: string) => (v.trim() === "" ? 0 : Number(v));

  // Status and paid date have to agree: marking paid stamps today when the date
  // is blank, and moving back to an unpaid status clears it.
  const setStatus = (status: string) =>
    setDraft((d) => {
      if (!d) return d;
      if (status === "paid")
        return { ...d, status, paid_date: d.paid_date ?? today() };
      if (UNPAID_STATUSES.has(status)) return { ...d, status, paid_date: null };
      return { ...d, status };
    });

  const persist = (extra: Partial<RentPayment> = {}) => {
    onUpdate({ id: payment.id, ...draft, ...extra });
  };

  const handleMarkPaid = () => {
    persist({ status: "paid", paid_date: draft.paid_date ?? today() });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button:last-of-type]:hidden"
      >
        <SheetTitle className="sr-only">
          Payment from {payment.tenant_name}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Edit the amount, dates, status, and method for this rent payment.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start gap-3.5 border-b border-border px-6 py-5">
          <PropertyThumbnail
            imageUrl={isSavedProperty ? property?.imageUrl : undefined}
            alt={propertyLabel ?? "Property"}
            fallback={<Home className="h-7 w-7" />}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="truncate text-lg font-extrabold text-foreground">
              {payment.tenant_name}
            </h2>
            {propertyLabel && (
              <div className="truncate text-sm text-muted-foreground">
                {propertyLabel}
              </div>
            )}
            {cityLine && (
              <div className="truncate text-sm text-muted-foreground">
                {cityLine}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold",
                  cfg.chip,
                )}
              >
                {cfg.icon} {cfg.label}
              </span>
              <span className="text-[11px] text-muted-foreground">
                Due {prettyDate(payment.due_date)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-md text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="min-w-0 flex-1 space-y-3.5 overflow-y-auto px-6 py-5">
          {/* Status overview */}
          <section className={cn("rounded-lg border p-4", cfg.accent)}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-bold text-foreground">
                {cfg.headline}
              </span>
              <span className="text-sm font-bold tabular-nums text-foreground">
                {money(draft.amount + draft.late_fee)} total
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <StatTile label="Rent" value={money(draft.amount)} />
              <StatTile
                label="Late fee"
                value={money(draft.late_fee)}
                valueClassName={
                  draft.late_fee > 0 ? "text-destructive" : undefined
                }
              />
              <StatTile
                label="Paid on"
                value={prettyDate(draft.paid_date)}
                valueClassName={draft.paid_date ? "text-success" : undefined}
              />
            </div>
          </section>

          {/* Tenant & property */}
          <Section
            icon={<Home className="h-3.5 w-3.5" />}
            title="Tenant & property"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rp-tenant">Tenant Name</Label>
                <Input
                  id="rp-tenant"
                  className="bg-card"
                  value={draft.tenant_name}
                  onChange={(e) => set("tenant_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-property">Property</Label>
                <Select
                  value={draft.pipeline_property_id}
                  onValueChange={(v) => set("pipeline_property_id", v)}
                >
                  <SelectTrigger id="rp-property" className="bg-card">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyOptions.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          {/* Amounts */}
          <Section icon={<Wallet className="h-3.5 w-3.5" />} title="Amounts">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rp-amount">Amount ($)</Label>
                <Input
                  id="rp-amount"
                  type="number"
                  min="0"
                  className="bg-card"
                  value={draft.amount || ""}
                  onChange={(e) => set("amount", numOrZero(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-late-fee">Late Fee ($)</Label>
                <Input
                  id="rp-late-fee"
                  type="number"
                  min="0"
                  className="bg-card"
                  placeholder="0"
                  value={draft.late_fee || ""}
                  onChange={(e) => set("late_fee", numOrZero(e.target.value))}
                />
              </div>
            </div>
            {!amountValid && (
              <p className="mt-3 text-xs text-destructive">
                Enter an amount greater than $0.
              </p>
            )}
          </Section>

          {/* Schedule & status */}
          <Section
            icon={<Clock className="h-3.5 w-3.5" />}
            title="Schedule & status"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rp-due">Due Date</Label>
                <DateInput
                  id="rp-due"
                  className="bg-card"
                  value={draft.due_date}
                  onChange={(e) => set("due_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-paid">Paid Date</Label>
                <DateInput
                  id="rp-paid"
                  className="bg-card"
                  value={draft.paid_date ?? ""}
                  onChange={(e) => set("paid_date", e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={draft.status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-card">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select
                  value={draft.payment_method ?? ""}
                  onValueChange={(v) => set("payment_method", v)}
                >
                  <SelectTrigger className="bg-card">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Paid payments count as rental income on your Properties and
              Accounting pages.
            </p>
          </Section>

          {/* Notes */}
          <Section icon={<NotebookPen className="h-3.5 w-3.5" />} title="Notes">
            <Textarea
              value={draft.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Partial amounts, payment plans, follow-ups…"
              className="min-h-[72px] resize-y bg-card"
            />
          </Section>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border bg-card px-6 py-3.5">
          {isDirty && (
            <span className="text-xs text-muted-foreground">
              Unsaved changes
            </span>
          )}
          {draft.status !== "paid" && (
            <Button
              variant="outline"
              className="ml-auto"
              onClick={handleMarkPaid}
              disabled={isSaving || !canSave}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Paid
            </Button>
          )}
          <Button
            className={cn(draft.status === "paid" && "ml-auto")}
            onClick={() => persist()}
            disabled={isSaving || !canSave || !isDirty}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            Changes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
